import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { Compass, Loader2, TrendingUp, TrendingDown, Target, Zap, ArrowUpRight, RefreshCw } from "lucide-react";

const SIGNAL_META = {
  "on-track": { color: "text-[#34C759]", bg: "bg-[#34C759]", label: "On track", Icon: Target },
  "under-paid": { color: "text-[#FF3B30]", bg: "bg-[#FF3B30]", label: "Under-paid", Icon: TrendingDown },
  "ambitious": { color: "text-[#0033FF]", bg: "bg-[#0033FF]", label: "Ambitious", Icon: TrendingUp },
};

export default function CareerGPS() {
  const { t } = useI18n();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/career/gps");
      setData(r.data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  if (loading) {
    return (
      <div className="flex items-center gap-3 py-20 text-sm font-mono text-gray-500">
        <Loader2 className="w-4 h-4 animate-spin" /> Reading the market…
      </div>
    );
  }

  if (!data || !data.role) {
    return (
      <div className="space-y-8 max-w-3xl" data-testid="career-gps-page">
        <div>
          <div className="font-mono text-xs uppercase tracking-[0.2em] text-gray-500 mb-3">/ {t("career_gps")}</div>
          <h1 className="font-display font-black text-4xl md:text-5xl tracking-tighter">Career GPS</h1>
        </div>
        <div className="border border-dashed border-black/20 p-10 text-sm text-gray-600">
          Add at least one job title in your{" "}
          <a href="/app/profile" className="underline font-bold">profile</a> to unlock your personalized
          market map.
        </div>
      </div>
    );
  }

  const meta = SIGNAL_META[data.signal] || SIGNAL_META["on-track"];
  const SignalIcon = meta.Icon;

  return (
    <div className="space-y-8 max-w-5xl" data-testid="career-gps-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="font-mono text-xs uppercase tracking-[0.2em] text-gray-500 mb-3">
            / {t("career_gps")}
          </div>
          <h1 className="font-display font-black text-4xl md:text-5xl tracking-tighter">
            {data.role || "Your role"}
          </h1>
          <p className="text-gray-600 mt-3 max-w-2xl">
            Live market intel from {data.sample_size || 0} postings — refreshed on every visit.
          </p>
        </div>
        <button
          onClick={load}
          className="border border-black/15 hover:bg-black hover:text-white px-4 py-2 text-sm font-bold flex items-center gap-2 transition-colors"
          data-testid="gps-refresh"
        >
          <RefreshCw className="w-3.5 h-3.5" /> Refresh
        </button>
      </div>

      {/* Signal banner */}
      <div
        className={`${meta.bg} text-white p-8 flex items-center gap-5`}
        data-testid="gps-signal"
      >
        <div className="w-16 h-16 bg-white/15 flex items-center justify-center">
          <SignalIcon className="w-8 h-8" />
        </div>
        <div className="flex-1">
          <div className="font-mono text-[10px] uppercase tracking-widest text-white/70 mb-1">Signal</div>
          <div className="font-display font-black text-3xl">{meta.label}</div>
          <div className="text-sm text-white/85 mt-1">
            {data.signal === "under-paid" &&
              `You could be earning ${Math.abs(data.salary_gap_pct)}% more. Market median is SAR ${Number(
                data.market_median_sar
              ).toLocaleString()}.`}
            {data.signal === "on-track" &&
              `Your target lines up with the market median of SAR ${Number(
                data.market_median_sar
              ).toLocaleString()}.`}
            {data.signal === "ambitious" &&
              `You're aiming ${Math.abs(data.salary_gap_pct)}% above the median — defensible with strong leverage.`}
          </div>
        </div>
      </div>

      {/* Salary stats */}
      <div className="grid sm:grid-cols-3 gap-0 border border-black/10">
        <div className="p-6 border-e border-black/10">
          <div className="font-mono text-[10px] uppercase tracking-widest text-gray-500 mb-2">Market min</div>
          <div className="font-display font-black text-3xl">
            SAR {Number(data.market_range?.min || 0).toLocaleString()}
          </div>
        </div>
        <div className="p-6 border-e border-black/10 bg-[#F8F9FA]">
          <div className="font-mono text-[10px] uppercase tracking-widest text-[#0033FF] mb-2">Median</div>
          <div className="font-display font-black text-3xl text-[#0033FF]">
            SAR {Number(data.market_median_sar || 0).toLocaleString()}
          </div>
        </div>
        <div className="p-6">
          <div className="font-mono text-[10px] uppercase tracking-widest text-gray-500 mb-2">Market max</div>
          <div className="font-display font-black text-3xl text-[#34C759]">
            SAR {Number(data.market_range?.max || 0).toLocaleString()}
          </div>
        </div>
      </div>

      {/* Target vs median */}
      <div className="border border-black/10 bg-white p-6 space-y-3">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-widest text-gray-500 mb-1">Your target</div>
            <div className="font-display font-black text-2xl">
              SAR {Number(data.target_salary_sar || 0).toLocaleString()}
            </div>
          </div>
          <div className={`font-mono text-sm font-bold ${meta.color}`}>
            Gap: {data.salary_gap_pct > 0 ? "+" : ""}
            {data.salary_gap_pct}%
          </div>
        </div>
        {(!data.target_salary_sar || data.target_salary_sar === 0) && (
          <div className="text-xs text-gray-600">
            Set a monthly target in your{" "}
            <a href="/app/profile" className="underline font-bold">profile</a> for a sharper gap analysis.
          </div>
        )}
      </div>

      {/* Skill gap */}
      <div>
        <div className="flex items-center gap-3 mb-4">
          <Zap className="w-5 h-5 text-[#0033FF]" />
          <h2 className="font-display font-bold text-2xl">Skills the market is paying for</h2>
        </div>
        {data.missing_skills && data.missing_skills.length > 0 ? (
          <div className="flex flex-wrap gap-2">
            {data.missing_skills.map((s) => (
              <span
                key={s}
                className="px-4 py-2 border border-black text-sm font-bold uppercase tracking-wider bg-white hover:bg-black hover:text-white transition-colors"
                data-testid={`gps-skill-${s}`}
              >
                {s}
              </span>
            ))}
          </div>
        ) : (
          <div className="text-sm text-gray-600 border border-dashed border-black/15 p-6">
            Your skill stack aligns with current postings. Nice.
          </div>
        )}
      </div>

      {/* Recommendations */}
      <div className="border border-black/10 bg-[#F8F9FA] p-6">
        <div className="flex items-center gap-3 mb-4">
          <Compass className="w-5 h-5 text-[#0033FF]" />
          <h3 className="font-display font-bold text-xl">Next moves</h3>
        </div>
        <ul className="space-y-3">
          {(data.recommendations || []).map((r, i) => (
            <li key={i} className="flex gap-3 text-sm leading-relaxed" data-testid={`gps-rec-${i}`}>
              <ArrowUpRight className="w-4 h-4 text-[#0033FF] mt-0.5 shrink-0" />
              <span>{r}</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
